import { C as Cart } from './cart-BCBag437.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import { f as fetchHTML } from './fetchHTML-9PhJW9Dj.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';
import { S as Swish } from './swish-DQTyiib4.js';

const ITEM_SECTION = 'fetch-wishlist-item';

class WishlistItems extends HTMLElement {
  #list = this.querySelector('[data-wishlist-list]');
  #emptyState = this.querySelector('[data-wishlist-empty]');
  #loadingOverlay = this.querySelector('.loading-overlay');
  #cards = new Map();
  #items;
  #open = false;
  #renderToken = 0;
  #removed = new Set();
  #subscription;
  #unsubscribeTab;

  connectedCallback() {
    this.addEventListener('click', this.#onClick, true);
    this.addEventListener('keydown', this.#onKeydown, true);
    this.addEventListener('change', (event) => event.stopPropagation(), true);

    this.#unsubscribeTab = Pubsub.subscribe('cart:tab', ({ data }) => {
      this.#open = data.tab === 'wishlist';
      if (this.#open) this.#render();
    });

    if (!this.#subscription) this.#observe();
  }

  disconnectedCallback() {
    this.#subscription?.unsubscribe();
    this.#subscription = null;
    this.#unsubscribeTab?.();
  }

  async #observe() {
    if (!Swish.available) return;

    try {
      this.#subscription = await Swish.observe((wishlist) => this.#onWishlist(wishlist));
    } catch (error) {
      console.error('Failed to load wishlist items', error);
    }
  }

  /**
   * Take in the wishlist, on load and after every change. The count goes out either way — the tab
   * carries it — but the cards cost a section request each, so those wait until the tab is open.
   *
   * A refetch that started before a removal still lists that item, in its items and in its count,
   * so both are corrected for what was removed here. Optimistically added items carry a
   * placeholder product without a title: they count towards the total but have no card yet, and
   * the product data follows in the next emission.
   * @param {object} wishlist - The Swish wishlist (`{ numItems, items }`)
   */
  #onWishlist(wishlist) {
    const items = wishlist.items.filter((item) => !this.#removed.has(item.id));
    const stale = wishlist.items.length - items.length;

    Pubsub.publish('wishlist:count', { data: { count: Math.max(wishlist.numItems - stale, 0) } });

    this.#items = items.filter((item) => item.product?.title);

    if (this.#open) this.#render();
  }

  /**
   * Render the wishlist, one card per item
   */
  async #render() {
    const token = ++this.#renderToken;

    if (!this.#items) {
      this.#loadingOverlay.ariaHidden = false;
      return;
    }

    const items = this.#items;

    this.#emptyState.hidden = items.length > 0;
    this.#list.hidden = items.length === 0;

    if (items.length === 0) {
      this.#list.replaceChildren();
      this.#loadingOverlay.ariaHidden = true;
      return;
    }

    if (!this.#list.hasChildNodes()) this.#loadingOverlay.ariaHidden = false;

    const cards = await Promise.all(items.map((item) => this.#createCard(item)));

    // A newer render took over while these were in flight, and its list is the current one
    if (token !== this.#renderToken) return;

    this.#cards = new Map(
      items.map(({ id }, index) => [id, cards[index]]).filter(([, card]) => card)
    );
    this.#list.replaceChildren(...cards.filter(Boolean));
    this.#loadingOverlay.ariaHidden = true;
  }

  /**
   * Fetch an item's card, once
   * @param {object} item - A Swish wishlist item (`{ id, variantId, product }`)
   * @returns {Promise<Element|null>} - The list item, or null when its card couldn't be fetched
   */
  async #createCard(item) {
    if (this.#cards.has(item.id)) return this.#cards.get(item.id);

    try {
      const html = await fetchHTML(`${item.product.url}?section_id=${ITEM_SECTION}`);
      const card = html.querySelector('[data-wishlist-item]');
      if (!card) throw new Error(`No card in ${ITEM_SECTION}`);

      card.dataset.wishlistItem = item.id;

      const li = document.createElement('li');
      li.append(card);

      return li;
    } catch (error) {
      console.error("Couldn't load wishlist item", error);
      Notyf.error(window.toast_translations.wishlist.fetch_error);
      return null;
    }
  }

  /**
   * Treat enter on a size as a pick. Left alone it reaches `product-variant-select`, which
   * refetches its section and rewrites the URL of a product page that happens to be open.
   * Buttons are left to the browser, which turns enter into a click of its own.
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !event.target.closest('.product-option')) return;

    this.#onClick(event);
  };

  /**
   * Handle every click in the list. The card is the hook to go by: the options are list items of
   * their own, so `closest('li')` would land on a size instead of the card it belongs to. Only
   * single-variant products carry a variant on their add button, the rest expand their options.
   * @param {PointerEvent} event - The click event
   */
  #onClick = (event) => {
    const card = event.target.closest('[data-wishlist-item]');
    if (!card) return;

    // One action at a time: the overlay is up for as long as the card is busy
    if (card.querySelector('.loading-overlay').ariaHidden === 'false') return;

    if (event.target.closest('[data-wishlist-remove]')) {
      this.#removeItem(card.dataset.wishlistItem, card.querySelector('.loading-overlay'));
      return;
    }

    const addButton = event.target.closest('[data-wishlist-add]');
    if (!addButton) {
      this.#onOptionPick(event, card);
      return;
    }

    const { variantId } = addButton.dataset;
    variantId ? this.#addToCart(card, variantId) : this.#toggleOptions(card, addButton);
  };

  /**
   * Expand or collapse an item's options
   * @param {Element} card - The item's card
   * @param {Element} addButton - The item's add button
   */
  #toggleOptions(card, addButton) {
    const options = card.querySelector('[data-wishlist-options]');
    const expanded = addButton.ariaExpanded === 'false';

    addButton.ariaExpanded = String(expanded);
    options.ariaHidden = String(!expanded);
    options.inert = !expanded;
  }

  /**
   * Add the picked option's variant to the cart. The radio sits behind its label, so the click
   * lands on either of the two, and checking it is ours to do — the default would forward the
   * label click to the radio, firing a second click that adds the item twice.
   * @param {PointerEvent} event - The click event
   * @param {Element} card - The item's card
   */
  #onOptionPick(event, card) {
    const option = event.target.closest('.product-option')?.querySelector('[data-variant-id]');
    if (!option) return;
    event.preventDefault();
    event.stopPropagation();
    option.checked = true;

    if (option.dataset.outOfStock === 'true') {
      Notyf.error(window.toast_translations.product.sold_out);
      return;
    }

    this.#addToCart(card, option.dataset.variantId);
  }

  /**
   * Add a variant to the cart and take the item off the wishlist
   * @param {Element} card - The item's card
   * @param {string} variantId - The variant to add
   */
  async #addToCart(card, variantId) {
    const loadingOverlay = card.querySelector('.loading-overlay');
    const wishlistItemId = card.dataset.wishlistItem;

    loadingOverlay.ariaHidden = false;

    try {
      await Cart.add({ items: [{ id: +variantId, quantity: 1 }] });
    } catch (error) {
      console.error("Couldn't add wishlist item to cart", error);
      Notyf.error(window.toast_translations.wishlist.add_error);
      loadingOverlay.ariaHidden = true;
      return;
    }

    this.#removeItem(wishlistItemId, loadingOverlay);
  }

  /**
   * Remove an item from the wishlist. The wishlist observer re-renders the list.
   *
   * The service queues mutations, so a removal can't overlap with one the shopper started a moment
   * earlier — adding a size to the cart removes its item once the cart call comes back, which
   * lands in the middle of whatever else is running.
   * @param {string} wishlistItemId - The Swish wishlist item to remove
   * @param {Element} loadingOverlay - The overlay of the card it belongs to
   */
  async #removeItem(wishlistItemId, loadingOverlay) {
    loadingOverlay.ariaHidden = false;

    try {
      await Swish.remove(wishlistItemId);

      this.#removed.add(wishlistItemId);
      this.#items = this.#items?.filter(({ id }) => id !== wishlistItemId);
      if (this.#open) this.#render();
    } catch (error) {
      console.error("Couldn't remove wishlist item", error);
      Notyf.error(window.toast_translations.wishlist.remove_error);
    } finally {
      loadingOverlay.ariaHidden = true;
    }
  }
}

customElements.define('wishlist-items', WishlistItems);
